import type { ScheduleTimezone, Weekday } from '@foresightdata/sourcing-contracts';
import * as yup from 'yup';

export type ScheduleFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY';

export type { ScheduleTimezone, Weekday };

const WEEKDAY_VALUES: readonly Weekday[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

const TIMEZONE_VALUES: readonly ScheduleTimezone[] = ['ET', 'CT', 'MT', 'PT'];

export interface ScheduleConfig {
  readonly frequency: ScheduleFrequency;
  readonly startAtUtc: string;
  readonly timezone: ScheduleTimezone;
  readonly daysOfWeek: Weekday[] | null;
  readonly dayOfMonth: number | null;
}

/**
 * In-progress schedule in the settings dialog before the user has chosen a
 * frequency. Distinct from `ScheduleConfig` so the rest of the codebase can
 * keep relying on `frequency: ScheduleFrequency` (non-null) without null
 * guards everywhere; `isScheduleValid` narrows back to `ScheduleConfig`
 * before the payload is built.
 */
export interface ScheduleDraft {
  readonly frequency: null;
  readonly startAtUtc: string | null;
  readonly timezone: ScheduleTimezone | null;
  readonly daysOfWeek: Weekday[] | null;
  readonly dayOfMonth: number | null;
}

export type ScheduleValue = ScheduleConfig | ScheduleDraft | null;

export type ApiRefreshScheduleFields = Pick<
  ApiList,
  | 'refreshScheduleFrequency'
  | 'refreshScheduleStartAtUtc'
  | 'refreshScheduleTimezone'
  | 'refreshScheduleDaysOfWeek'
  | 'refreshScheduleDayOfMonth'
>;

export type ApiEnrichScheduleFields = Pick<
  ApiList,
  | 'enrichScheduleFrequency'
  | 'enrichScheduleStartAtUtc'
  | 'enrichScheduleTimezone'
  | 'enrichScheduleDaysOfWeek'
  | 'enrichScheduleDayOfMonth'
>;

export type ApiScheduleFields = ApiRefreshScheduleFields & ApiEnrichScheduleFields;

export interface ApiList {
  id: string;
  shortName: string;
  entityType: 'company' | 'person';
  type: 'STATIC' | 'ACTIVE';
  createdAt: string;
  updatedAt: string;
  query?: string | null;
  lastRefreshDate?: string | null;
  lastEnrichDate?: string | null;
  refreshScheduleFrequency?: ScheduleFrequency | null;
  refreshScheduleStartAtUtc?: string | null;
  refreshScheduleTimezone?: ScheduleTimezone | null;
  refreshScheduleDaysOfWeek?: Weekday[] | null;
  refreshScheduleDayOfMonth?: number | null;
  enrichScheduleFrequency?: ScheduleFrequency | null;
  enrichScheduleStartAtUtc?: string | null;
  enrichScheduleTimezone?: ScheduleTimezone | null;
  enrichScheduleDaysOfWeek?: Weekday[] | null;
  enrichScheduleDayOfMonth?: number | null;
}

// Yup shape + transform for the 8 schedule fields (4 per slice × refresh/enrich).
// Grouped here so adding a new schedule field touches a single adjacent pair instead of
// being diluted across the whole base shape.
const scheduleShape = {
  refreshScheduleFrequency: yup.mixed<ScheduleFrequency>().nullable().optional(),
  refreshScheduleStartAtUtc: yup.string().nullable().optional(),
  refreshScheduleTimezone: yup
    .string<ScheduleTimezone>()
    .oneOf([...TIMEZONE_VALUES])
    .nullable()
    .optional(),
  refreshScheduleDaysOfWeek: yup
    .array(
      yup
        .string<Weekday>()
        .oneOf([...WEEKDAY_VALUES])
        .required()
    )
    .nullable()
    .optional(),
  refreshScheduleDayOfMonth: yup.number().nullable().optional(),
  enrichScheduleFrequency: yup.mixed<ScheduleFrequency>().nullable().optional(),
  enrichScheduleStartAtUtc: yup.string().nullable().optional(),
  enrichScheduleTimezone: yup
    .string<ScheduleTimezone>()
    .oneOf([...TIMEZONE_VALUES])
    .nullable()
    .optional(),
  enrichScheduleDaysOfWeek: yup
    .array(
      yup
        .string<Weekday>()
        .oneOf([...WEEKDAY_VALUES])
        .required()
    )
    .nullable()
    .optional(),
  enrichScheduleDayOfMonth: yup.number().nullable().optional(),
};

const scheduleTransform = (orig: ApiList) => ({
  refreshScheduleFrequency: orig.refreshScheduleFrequency ?? null,
  refreshScheduleStartAtUtc: orig.refreshScheduleStartAtUtc ?? null,
  refreshScheduleTimezone: orig.refreshScheduleTimezone ?? null,
  refreshScheduleDaysOfWeek: orig.refreshScheduleDaysOfWeek ?? null,
  refreshScheduleDayOfMonth: orig.refreshScheduleDayOfMonth ?? null,
  enrichScheduleFrequency: orig.enrichScheduleFrequency ?? null,
  enrichScheduleStartAtUtc: orig.enrichScheduleStartAtUtc ?? null,
  enrichScheduleTimezone: orig.enrichScheduleTimezone ?? null,
  enrichScheduleDaysOfWeek: orig.enrichScheduleDaysOfWeek ?? null,
  enrichScheduleDayOfMonth: orig.enrichScheduleDayOfMonth ?? null,
});

const baseShape = {
  id: yup.string().required(),
  name: yup.string().required(),
  entityType: yup.mixed<'company' | 'person'>().required(),
  lastRefreshDate: yup.string().nullable().optional(),
  lastEnrichDate: yup.string().nullable().optional(),
  ...scheduleShape,
};

const baseTransform = (orig: ApiList) => ({
  id: orig.id,
  name: orig.shortName,
  entityType: orig.entityType,
  lastRefreshDate: orig.lastRefreshDate ?? null,
  lastEnrichDate: orig.lastEnrichDate ?? null,
  ...scheduleTransform(orig),
});

export const sourcingListSchema = yup
  .object({
    ...baseShape,
    listType: yup.string<'static'>().required(),
    updatedAt: yup.string().required(),
  })
  .transform((_val, orig: ApiList) => ({
    ...baseTransform(orig),
    listType: 'static',
    updatedAt: orig.updatedAt,
  }));

export type SourcingList = yup.InferType<typeof sourcingListSchema>;

export const sourcingSavedSearchSchema = yup
  .object({
    ...baseShape,
    listType: yup.string<'active'>().required(),
    query: yup.string().nullable().optional(),
  })
  .transform((_val, orig: ApiList) => ({
    ...baseTransform(orig),
    listType: 'active',
    query: orig.query ?? null,
  }));

export type SourcingSavedSearch = yup.InferType<typeof sourcingSavedSearchSchema>;

export const refreshListOutputSchema = yup.object({
  added: yup.number().required(),
  unchanged: yup.number().required(),
  lastRefreshDate: yup.string().required(),
});

export type IRefreshListOutput = yup.InferType<typeof refreshListOutputSchema>;
